'use client';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CustomScrollBarProps, DragState, ScrollMetrics } from './scrollbar.types';

const MIN_THUMB_SIZE_PX = 32;

export interface UseCustomScrollBarReturn {
  // Refs
  scrollRef: React.RefObject<HTMLDivElement | null>;
  trackVRef: React.RefObject<HTMLDivElement | null>;
  thumbVRef: React.RefObject<HTMLDivElement | null>;
  trackHRef: React.RefObject<HTMLDivElement | null>;
  thumbHRef: React.RefObject<HTMLDivElement | null>;
  scrollAreaId: string;

  // State
  overflowV: boolean;
  overflowH: boolean;
  ariaNowV: number;
  ariaMaxV: number;
  ariaNowH: number;
  ariaMaxH: number;

  // Handlers
  onScroll: () => void;
  startDrag: (e: React.MouseEvent<HTMLDivElement>, axis: 'x' | 'y') => void;
  recalculate: () => void;
}

export function useCustomScrollBar({
  thumbFixedWidth,
  thumbFixedHeight,
  trackInsetTopPx = 0,
  trackInsetBottomPx = 0,
  orientation = 'vertical',
}: Pick<
  CustomScrollBarProps,
  'thumbFixedWidth' | 'thumbFixedHeight' | 'trackInsetTopPx' | 'trackInsetBottomPx' | 'orientation'
>): UseCustomScrollBarReturn {
  // Core DOM refs
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const trackVRef = useRef<HTMLDivElement | null>(null);
  const thumbVRef = useRef<HTMLDivElement | null>(null);
  const trackHRef = useRef<HTMLDivElement | null>(null);
  const thumbHRef = useRef<HTMLDivElement | null>(null);
  const scrollAreaId = useId();

  // Overflow flags
  const [overflowV, setOverflowV] = useState(false);
  const [overflowH, setOverflowH] = useState(false);

  // Accessibility values
  const [ariaNowV, setAriaNowV] = useState(0);
  const [ariaMaxV, setAriaMaxV] = useState(0);
  const [ariaNowH, setAriaNowH] = useState(0);
  const [ariaMaxH, setAriaMaxH] = useState(0);

  const mountedRef = useRef(false);

  /**
   * Shared geometry metrics for both axes.
   */
  const metricsRef = useRef<ScrollMetrics>({
    // Vertical
    clientH: 0,
    scrollH: 0,
    scrollTop: 0,
    trackH: 0,
    thumbH: 0,
    thumbTop: 0,
    maxThumbTop: 0,

    // Horizontal
    clientW: 0,
    scrollW: 0,
    scrollLeft: 0,
    trackW: 0,
    thumbW: 0,
    thumbLeft: 0,
    maxThumbLeft: 0,
  });

  // Shared drag state
  const draggingRef = useRef<DragState>({
    axis: null,
    active: false,
    startPos: 0,
    startThumbPos: 0,
  });

  /* ---------------- Vertical Logic ---------------- */

  const applyThumbStyleV = useCallback((top: number, height: number) => {
    const el = thumbVRef.current;
    if (!el) return;
    el.style.height = `${height}px`;
    el.style.transform = `translate3d(0, ${top}px, 0)`;
  }, []);

  const applyTrackStyleV = useCallback((top: number, bottom: number) => {
    const el = trackVRef.current;
    if (!el) return;
    el.style.top = `${top}px`;
    el.style.bottom = `${bottom}px`;
    el.style.height = 'auto';
  }, []);

  const computeThumbV = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { clientHeight, scrollHeight, scrollTop } = el;
    const insetT = trackInsetTopPx | 0;
    const insetB = trackInsetBottomPx | 0;

    const trackTop = insetT;
    const trackBottom = insetB;
    const trackH = Math.max(0, clientHeight - insetT - insetB);

    applyTrackStyleV(trackTop, trackBottom);

    const overflowing = scrollHeight > clientHeight + 10;
    setOverflowV(overflowing);

    if (!overflowing || trackH <= 0) {
      applyThumbStyleV(0, 0);
      setAriaNowV(0);
      setAriaMaxV(0);
      metricsRef.current.trackH = trackH;
      return;
    }

    const heightRatio = clientHeight / scrollHeight;
    const thumbH = thumbFixedHeight || Math.max(MIN_THUMB_SIZE_PX, Math.floor(trackH * heightRatio));
    const maxThumbTop = Math.max(0, trackH - thumbH);
    const scrollRatio = scrollTop / Math.max(1, scrollHeight - clientHeight);
    const thumbTop = Math.min(maxThumbTop, Math.round(scrollRatio * maxThumbTop));

    metricsRef.current = {
      ...metricsRef.current,
      clientH: clientHeight,
      scrollH: scrollHeight,
      scrollTop,
      trackH,
      thumbH,
      thumbTop,
      maxThumbTop,
    };

    applyThumbStyleV(thumbTop, thumbH);
    setAriaNowV(Math.round(scrollTop));
    setAriaMaxV(Math.max(1, scrollHeight - clientHeight));
  }, [applyThumbStyleV, applyTrackStyleV, trackInsetTopPx, trackInsetBottomPx, thumbFixedHeight]);

  /* ---------------- Horizontal Logic ---------------- */

  const applyThumbStyleH = useCallback((left: number, width: number) => {
    const el = thumbHRef.current;
    if (!el) return;
    el.style.width = `${width}px`;
    el.style.transform = `translate3d(${left}px, 0, 0)`;
  }, []);

  const computeThumbH = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { clientWidth, scrollWidth, scrollLeft } = el;

    const trackW = Math.max(0, clientWidth);
    const overflowing = scrollWidth > clientWidth + 10;
    setOverflowH(overflowing);

    if (!overflowing || trackW <= 0) {
      applyThumbStyleH(0, 0);
      setAriaNowH(0);
      setAriaMaxH(0);
      metricsRef.current.trackW = trackW;
      return;
    }

    const widthRatio = clientWidth / scrollWidth;
    const thumbW = thumbFixedWidth || Math.max(MIN_THUMB_SIZE_PX, Math.floor(trackW * widthRatio));
    const maxThumbLeft = Math.max(0, trackW - thumbW);
    const scrollRatio = scrollLeft / Math.max(1, scrollWidth - clientWidth);
    const thumbLeft = Math.min(maxThumbLeft, Math.round(scrollRatio * maxThumbLeft));

    metricsRef.current = {
      ...metricsRef.current,
      clientW: clientWidth,
      scrollW: scrollWidth,
      scrollLeft,
      trackW,
      thumbW,
      thumbLeft,
      maxThumbLeft,
    };

    applyThumbStyleH(thumbLeft, thumbW);
    setAriaNowH(Math.round(scrollLeft));
    setAriaMaxH(Math.max(1, scrollWidth - clientWidth));
  }, [applyThumbStyleH, thumbFixedWidth]);

  /* ---------------- Scroll Handling ---------------- */

  const computeAndUpdate = useCallback(() => {
    if (!scrollRef.current) return;
    if (orientation === 'vertical' || orientation === 'both') computeThumbV();
    if (orientation === 'horizontal' || orientation === 'both') computeThumbH();
  }, [orientation, computeThumbV, computeThumbH]);

  const onScroll = useCallback(() => {
    computeAndUpdate();
  }, [computeAndUpdate]);

  /* ---------------- Drag Handling ---------------- */

  const startDrag = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, axis: 'x' | 'y') => {
      e.preventDefault();
      draggingRef.current = {
        axis,
        active: true,
        startPos: axis === 'y' ? e.clientY : e.clientX,
        startThumbPos: axis === 'y' ? metricsRef.current.thumbTop : metricsRef.current.thumbLeft,
      };

      const onMove = (ev: MouseEvent) => {
        if (!draggingRef.current.active || draggingRef.current.axis !== axis) return;
        const el = scrollRef.current;
        if (!el) return;
        if (axis === 'y') {
          const deltaY = ev.clientY - draggingRef.current.startPos;
          const { trackH, thumbH } = metricsRef.current;
          const newThumbTop = Math.max(0, Math.min(trackH - thumbH, draggingRef.current.startThumbPos + deltaY));
          const ratio = newThumbTop / Math.max(1, trackH - thumbH);
          el.scrollTop = ratio * (metricsRef.current.scrollH - metricsRef.current.clientH);
          computeThumbV();
        } else {
          const deltaX = ev.clientX - draggingRef.current.startPos;
          const { trackW, thumbW } = metricsRef.current;
          const newThumbLeft = Math.max(0, Math.min(trackW - thumbW, draggingRef.current.startThumbPos + deltaX));
          const ratio = newThumbLeft / Math.max(1, trackW - thumbW);
          el.scrollLeft = ratio * (metricsRef.current.scrollW - metricsRef.current.clientW);
          computeThumbH();
        }
      };

      const onUp = () => {
        draggingRef.current.active = false;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove, { passive: true });
      document.addEventListener('mouseup', onUp, { passive: true });
    },
    [computeThumbV, computeThumbH],
  );

  /* ---------------- Lifecycle ---------------- */

  useLayoutEffect(() => {
    computeAndUpdate();
    requestAnimationFrame(() => {
      computeAndUpdate();
      requestAnimationFrame(() => {
        computeAndUpdate();
        mountedRef.current = true;
      });
    });
  }, [computeAndUpdate]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => computeAndUpdate());
    ro.observe(el);

    // Observe all children, not just the first one
    const observeChildren = () => {
      Array.from(el.children).forEach((child) => {
        ro.observe(child as Element);
      });
    };

    observeChildren();

    // Use MutationObserver to detect when children are added/removed
    const mo = new MutationObserver(() => {
      observeChildren();
      // Recalculate after DOM changes - use double RAF to ensure scrollHeight is updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          computeAndUpdate();
        });
      });
    });

    mo.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [computeAndUpdate]);

  return {
    scrollRef,
    trackVRef,
    thumbVRef,
    trackHRef,
    thumbHRef,
    scrollAreaId,
    overflowV,
    overflowH,
    ariaNowV,
    ariaMaxV,
    ariaNowH,
    ariaMaxH,
    onScroll,
    startDrag,
    recalculate: computeAndUpdate,
  };
}
